/**
 * NavItem Component
 * Individual navigation item with active state highlighting and tooltip when collapsed.
 */

import { Link, useRouterState } from '@tanstack/react-router';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface NavItemProps {
  /** Route path */
  to: string;
  /** Display label */
  label: string;
  /** Icon element */
  icon: React.ReactNode;
  /** Whether sidebar is collapsed */
  collapsed: boolean;
  /** Show a red dot badge on the icon */
  showBadge?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function NavItem({ to, label, icon, collapsed, showBadge }: NavItemProps) {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  // Check if this item is active (exact match or starts with path)
  const isActive = currentPath === to || currentPath.startsWith(to + '/');

  const linkContent = (
    <Link
      to={to}
      data-nav-to={to}
      className={cn(
        'flex flex-col items-center gap-0.5 px-1 py-2 rounded-md transition-colors',
        'text-[10px] font-medium text-center leading-tight',
        isActive
          ? 'bg-nav-active-bg text-sidebar-text-active'
          : 'text-sidebar-text hover:bg-nav-active-bg hover:text-sidebar-text-active'
      )}
    >
      <span className="relative shrink-0 [&>svg]:h-5 [&>svg]:w-5">
        {icon}
        {showBadge && (
          <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-sidebar-bg" />
        )}
      </span>
      {!collapsed && <span className="truncate max-w-full px-0.5">{label}</span>}
    </Link>
  );

  // Show tooltip when collapsed
  if (collapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return linkContent;
}

export default NavItem;
